<template>
  <CommandTemplateDetailsLayout>
    <template v-slot:detailsContent>
      <HeadingSmall>
        <IconButton
          :class="$style.backButton"
          data-test-id="TabView__BackIconButton"
          hasNoBackground
          icon="arrowLeft"
          text="Manage Command Templates"
          type="informational"
          @click="handleCancelClick"
        />
      </HeadingSmall>
      <CommandTemplateDetails />
      <CommandTemplateDetailsLaunch />
      <CommandTemplateDetailsOptions />
      <footer :class="$style.footer">
        <nav :class="$style.footerNav">
          <ButtonLink
            :class="$style.cancelButton"
            :isMatchText="true"
            text="cancel"
            textCase="lowercase"
            @click="handleCancelClick"
          />
          <Button
            :hasNoBackground="false"
            :hasOutline="false"
            :isDisabled="shouldDisableSave"
            :isLoading="false"
            :isMarketingStyle="false"
            :isMatchText="false"
            text="Save"
            textCase="capitalize"
            type="primary"
            @click="handleSaveTemplate"
          />
        </nav>
      </footer>
      <ModalEditCommandTemplate
        :item="item"
        :showModal="showModalEditCommandTemplate"
        @close="handleCancelClick"
        @save="handleSaveClick"
      />
    </template>
  </CommandTemplateDetailsLayout>
</template>

<script>
import {
  Button,
  ButtonLink,
  HeadingSmall,
  IconButton,
  Constants as uiComponentConstants,
} from '@jumpcloud/ui-components';
import { ItemTypeConsts, StoreModelMap, StoreModuleMap } from 'client/util/Constants';
import { mapActions, mapState } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import CommandTemplateDetails from 'client/Commands/CommandTemplateDetails/CommandTemplateDetails.vue';
import CommandTemplateDetailsLaunch from 'client/Commands/CommandTemplateDetails/CommandTemplateDetailsLaunch.vue';
import CommandTemplateDetailsLayout from 'client/Commands/CommandTemplateDetails/CommandTemplateDetailsLayout.vue';
import CommandTemplateDetailsOptions from 'client/Commands/CommandTemplateDetails/CommandTemplateDetailsOptions.vue';
import ModalEditCommandTemplate from 'client/Commands/Modals/ModalEditCommandTemplate';
import onlyPosixErrors from 'client/util/UserGroupAsideUtil';

const { notificationEmojis } = uiComponentConstants;
const commandTemplateModel = StoreModelMap[ItemTypeConsts.commandTemplate];
const commandCustomTemplateCollection = StoreModuleMap[ItemTypeConsts.commandCustomTemplate];

export default {
  name: 'CommandTemplateManageEdit',

  inject: ['$validator'],
  components: {
    Button,
    ButtonLink,
    CommandTemplateDetails,
    CommandTemplateDetailsLaunch,
    CommandTemplateDetailsLayout,
    CommandTemplateDetailsOptions,
    HeadingSmall,
    IconButton,
    ModalEditCommandTemplate,
  },
  data: () => ({
    showModalEditCommandTemplate: false,
  }),
  computed: {
    ...mapState(commandTemplateModel, [
      'item',
    ]),

    invalidCommand() {
      // Add custom validation error messages for command as it does not
      // write to v2errors object
      return this.item.command.trim() === '' || false;
    },

    invalidFields() {
      return !onlyPosixErrors(this.v2Errors.items);
    },

    shouldDisableSave() {
      return (
        this.invalidFields
        || this.invalidCommand
      )
        || false;
    },
  },
  mounted() {
    this.hanldeMounted();
  },

  beforeDestroy() {
    this.handleBeforeDestroy();
  },
  methods: {
    ...mapActions(commandTemplateModel, [
      'fetchItem',
      'saveItem',
      'resetItem',
      'setItemField',
      'setItemStateToUpdate',
    ]),

    ...mapActions(notificationModule, [
      'triggerErrorNotification',
      'triggerSuccessNotification',
    ]),

    ...mapActions(commandCustomTemplateCollection, [
      'fetchCollection',
    ]),

    handleBeforeDestroy() {
      this.resetItem();
    },

    handleCancelClick() {
      this.$router.push('/commands/managetemplates');
    },

    async handleSaveClick() {
      this.loadingEditTemplate = true;
      try {
        await this.saveItem();
        await this.fetchCollection();
        this.triggerSuccessNotification({
          message: 'You\'ve successfully saved the command template.',
          showCloseButton: false,
          title: 'Success',
          icon: '',
          emoji: notificationEmojis.success,
        });
      } catch {
        this.triggerErrorNotification({
          message: 'Unable to edit command template.',
          showCloseButton: true,
          title: 'Error',
          icon: '',
          emoji: notificationEmojis.error,
        });
      } finally {
        this.loadingEditTemplate = false;
        this.showModalEditCommandTemplate = false;
      }
    },
    async handleSaveTemplate() {
      await this.$validator.validate();
      if (!this.$validator.errors.any()) {
        this.showModalEditCommandTemplate = true;
      } else {
        this.triggerErrorNotification({
          message: 'Please review your settings for missing or invalid information.',
          showCloseButton: false,
          title: 'Error',
          icon: '',
          emoji: notificationEmojis.error,
        });
      }
    },
    async hanldeMounted() {
      const { id } = this.$route.params;
      if (id) {
        this.setItemField({ key: 'id', value: id });
        await this.fetchItem(id);
        this.setItemStateToUpdate();
      }
    },
  },
};
</script>

<style module scoped>
.backButton.backButton {
  margin-bottom: 0;
  margin-left: 0.5em;
}

.footer {
  background-color: var(--jc-background-light);
  border-top: var(--jc-border);
  bottom: 0;
  left: 50;
  padding: 15px 10px;
  position: fixed;
  width: 85%;
}

.footerNav {
  align-items: center;
  display: flex;
  justify-content: flex-end;
  margin-right: 1em;
  right: 20%;
}

.cancelButton {
  margin-right: 10px;
}

@media (max-width: 1900px) {
  .footerNav {
    margin-right: 2em;
  }
}

@media (max-width: 1400px) {
  .footerNav {
    margin-right: 6em;
  }
}
</style>
